import {
  EntityConfig,
  FilterContext,
  FilterResult,
} from '@/sections/insights/queryBuilder/legacy/types/types';

export const ACTIVE_DIRECTORY_RELATIONSHIP_LABELS = [
  'MemberOf',
  'AdminTo',
  'HasSession',
  'Contains',
  'CanPSRemote',
  'CanRDP',
  'ExecuteDCOM',
  'AllowedToDelegate',
  'Owns',
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',
  'AddMember',
  'ForceChangePassword',
  'DCFor',
  'TrustedBy',
  'TrustedFor',
  'HasSIDHistory',
  'AllowedToAct',
  'AllExtendedRights',
  'ReadLAPSPassword',
  'GPLink',
  'AddAllowedToAct',
  'SameForestTrust',
  'CrossForestTrust',
  'CoerceToTGT',
  'GetChanges',
  'GetChangesAll',
  'GetChangesInFilteredSet',
  'SpoofSIDHistory',
  'AbuseTGTDelegation',
  'AddSelf',
  'DCSync',
  'ReadGMSAPassword',
  'DumpSMSAPassword',
  'SQLAdmin',
  'WriteSPN',
  'AddKeyCredentialLink',
  'LocalToComputer',
  'MemberOfLocalGroup',
  'RemoteInteractiveLogonRight',
  'SyncLAPSPassword',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'RootCAFor',
  'PublishedTo',
  'ManageCertificates',
  'ManageCA',
  'DelegatedEnrollmentAgent',
  'Enroll',
  'HostsCAService',
  'WritePKIEnrollmentFlag',
  'WritePKINameFlag',
  'NTAuthStoreFor',
  'TrustedForNTAuth',
  'EnterpriseCAFor',
  'IssuedSignedBy',
  'GoldenCert',
  'EnrollOnBehalfOf',
  'OIDGroupLink',
  'ExtendedByPolicy',
  'ADCSESC1',
  'ADCSESC3',
  'ADCSESC4',
  'ADCSESC6a',
  'ADCSESC6b',
  'ADCSESC9a',
  'ADCSESC9b',
  'ADCSESC10a',
  'ADCSESC10b',
  'ADCSESC13',
  'SyncedToEntraUser',
  'CoerceAndRelayNTLMToSMB',
  'CoerceAndRelayNTLMToADCS',
  'CoerceAndRelayNTLMToLDAP',
  'CoerceAndRelayNTLMToLDAPS',
  'WriteOwnerLimitedRights',
  'WriteOwnerRaw',
  'OwnsLimitedRights',
  'OwnsRaw',
  'ClaimSpecialIdentity',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
] as const;

export type ActiveDirectoryRelationshipLabel =
  (typeof ACTIVE_DIRECTORY_RELATIONSHIP_LABELS)[number];

export const ACTIVE_DIRECTORY_RELATIONSHIP_TARGET_MAP: Record<
  ActiveDirectoryRelationshipLabel,
  'ActiveDirectory'
> = Object.fromEntries(
  ACTIVE_DIRECTORY_RELATIONSHIP_LABELS.map(label => [label, 'ActiveDirectory'])
) as Record<ActiveDirectoryRelationshipLabel, 'ActiveDirectory'>;

export const ActiveDirectoryEntityConfig: EntityConfig = {
  type: 'ActiveDirectory',
  label: 'Active Directory',
  neo4jLabels: ['ADObject'],

  fields: {
    direct: [
      'key',
      'dns',
      'name',
      'domain',
      'status',
      'class',
      'samaccountname',
      'displayname',
      'description',
      'distinguishedname',
      'objectid',
      'operatingsystem',
      'admincount',
      'isaclprotected',
      'pwdneverexpires',
      'isdc',
      'unconstraineddelegation',
      'ldapavailable',
      'ldapsavailable',
      'authenticationenabled',
      'adminsdholderprotected',
      'blocksinheritance',
      'dontreqpreauth',
      'passwordexpired',
      'encryptedtextpwdallowed',
      'smartcardrequired',
      'trustedtoauth',
      'enrolleesuppliessubject',
      'requiresmanagerapproval',
      'sensitive',
      'adObjectType',
      'hasspn',
    ],
    attributes: ['port', 'software'],
  },

  filterMappings: {
    Status: 'status',
    Class: 'class',
    Name: 'name',
    'SAM Account Name': 'samaccountname',
    'Display Name': 'displayname',
    Description: 'description',
    'Object ID': 'objectid',
    Domain: 'domain',
    'Distinguished Name': 'distinguishedname',
    'Admin Count': 'admincount',
    'Operating System': 'operatingsystem',
    'ACL Protected': 'isaclprotected',
    'Password Never Expires': 'pwdneverexpires',
    'Is DC': 'isdc',
    'Unconstrained Delegation': 'unconstraineddelegation',
    'LDAP Available': 'ldapavailable',
    'LDAPS Available': 'ldapsavailable',
    'Authentication Enabled': 'authenticationenabled',
    'AdminSDHolder Protected': 'adminsdholderprotected',
    'Blocks Inheritance': 'blocksinheritance',
    "Don't Require Preauth": 'dontreqpreauth',
    'Password Expired': 'passwordexpired',
    'Encrypted Text Password Allowed': 'encryptedtextpwdallowed',
    'Smart Card Required': 'smartcardrequired',
    'Trusted To Auth': 'trustedtoauth',
    'Enrollee Supplies Subject': 'enrolleesuppliessubject',
    'Requires Manager Approval': 'requiresmanagerapproval',
    Sensitive: 'sensitive',
    'AD Object Type': 'adObjectType',
  },

  specialHandlers: {
    adObjectType: handleADObjectTypeField,

    admincount: handleADBooleanField,
    isaclprotected: handleADBooleanField,
    pwdneverexpires: handleADBooleanField,
    isdc: handleADBooleanField,
    unconstraineddelegation: handleADBooleanField,
    ldapavailable: handleADBooleanField,
    ldapsavailable: handleADBooleanField,
    authenticationenabled: handleADBooleanField,
    adminsdholderprotected: handleADBooleanField,
    blocksinheritance: handleADBooleanField,
    dontreqpreauth: handleADBooleanField,
    passwordexpired: handleADBooleanField,
    encryptedtextpwdallowed: handleADBooleanField,
    smartcardrequired: handleADBooleanField,
    trustedtoauth: handleADBooleanField,
    enrolleesuppliessubject: handleADBooleanField,
    requiresmanagerapproval: handleADBooleanField,
    sensitive: handleADBooleanField,
    hasspn: handleADBooleanField,
  },

  validation: {
    required: [],
    optional: ['adObjectType', 'name', 'samaccountname', 'class'],
  },

  relationships: {
    Asset: [
      {
        path: [
          {
            label: 'HAS_ATTRIBUTE',
            direction: 'forward',
          },
        ],
      },
    ],
    Vulnerability: [
      {
        path: [
          {
            label: ['HAS_VULNERABILITY', 'HAS_ATTRIBUTE'],
            direction: 'forward',
            length: 2,
          },
        ],
      },
    ],
    Technology: [
      {
        path: [
          {
            label: 'HAS_ATTRIBUTE',
            direction: 'forward',
            intermediate: 'Attribute',
          },
          { label: 'HAS_TECHNOLOGY', direction: 'forward' },
        ],
      },
    ],
    Seed: [
      {
        path: [{ label: 'DISCOVERED', direction: 'reverse' }],
      },
    ],

    ActiveDirectory: [
      {
        path: [
          {
            label: Array.from(ACTIVE_DIRECTORY_RELATIONSHIP_LABELS),
            direction: 'forward',
          },
        ],
      },
    ],
  },
};

function handleADObjectTypeField(context: FilterContext): FilterResult {
  const { value, isNot } = context;

  const adTypes = Array.isArray(value)
    ? value.map(v => String(v)).filter(Boolean)
    : [String(value)].filter(Boolean);

  if (adTypes.length === 0) {
    return {};
  }

  return {
    directFilter:
      adTypes.length <= 1
        ? {
            field: 'adObjectType',
            operator: '=',
            value: adTypes[0],
            not: Boolean(isNot),
          }
        : {
            field: '',
            operator: isNot ? 'AND' : 'OR',
            value: adTypes.map(type => ({
              field: 'adObjectType',
              operator: '=',
              value: type,
              not: Boolean(isNot),
            })),
          },
  };
}

function handleADBooleanField(context: FilterContext): FilterResult {
  const { field, value, isNot } = context;

  if (Array.isArray(value)) {
    const hasNoValue = value.some(v => String(v) === 'false');
    const hasTrueValue = value.some(v => String(v) === 'true');

    if (hasNoValue && !hasTrueValue) {
      return {
        directFilter: {
          field: field,
          operator: '=',
          value: true,
          not: true,
        },
        isNot: false,
      };
    } else if (hasTrueValue && !hasNoValue) {
      return {
        directFilter: {
          field: field,
          operator: '=',
          value: true,
          not: isNot,
        },
        isNot: false,
      };
    } else {
      const booleanValue = value.map(v => String(v) === 'true');
      return {
        directFilter: {
          field: field,
          operator: '=',
          value: booleanValue,
          not: isNot,
        },
        isNot: false,
      };
    }
  } else {
    if (String(value) === 'false') {
      if (isNot) {
        return {
          directFilter: {
            field: field,
            operator: '=',
            value: false,
            not: true,
          },
          isNot: false,
        };
      } else {
        return {
          directFilter: {
            field: field,
            operator: '=',
            value: true,
            not: true,
          },
          isNot: false,
        };
      }
    } else {
      return {
        directFilter: {
          field: field,
          operator: '=',
          value: true,
          not: isNot,
        },
        isNot: false,
      };
    }
  }
}
